import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Stack,
  CircularProgress,
  Alert,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { getData } from "../../Webservice/Webservice";

/**
 * Helper: convert ISO date string -> short relative time (e.g. "2 hours ago")
 */
function timeAgo(isoDate) {
  if (!isoDate) return "";
  const now = new Date();
  const then = new Date(isoDate);
  const seconds = Math.floor((now - then) / 1000);
  if (seconds < 60) return `${seconds} sec${seconds > 1 ? "s" : ""} ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min${minutes > 1 ? "s" : ""} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days > 1 ? "s" : ""} ago`;
  // fallback to date
  return then.toLocaleDateString();
}

function Notification() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;

    async function fetchNotifications() {
      setLoading(true);
      setError(null);
      try {
        const tokenValue = sessionStorage.getItem("token");
        const token = tokenValue ? `Bearer ${tokenValue}` : "";
        const res = await getData("notification/customer",null , token);
        const items = (res && res.data) || [];
        items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        if (mounted) setNotifications(items);
      } catch (err) {
        console.error("Error fetching notifications:", err);
        if (mounted) setError("Failed to load notifications.");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    fetchNotifications();

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <Box
      sx={{
        maxWidth: "900px",
        mx: "auto",
        p: { xs: 2, md: 4 },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Notifications
        </Typography>
      </Stack>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && notifications.length === 0 && (
        <Alert severity="info">No notifications yet.</Alert>
      )}

      {!loading &&
        !error &&
        notifications.map((item) => (
          <Card
            key={item._id}
            sx={{
              mb: 2,
              borderRadius: "16px",
              border: "1px solid #e0e0e0",
              backgroundColor: item.isRead ? "#fafafa" : "#ebedefff",
              transition: "0.3s",
              "&:hover": {
                transform: "translateY(-3px)",
                boxShadow: "0 6px 16px rgba(0,0,0,0.1)",
              },
            }}
          >
            <CardContent>
              <Stack direction="row" spacing={2}>
                <Avatar sx={{ bgcolor: "#000000ff" }}>
                  <NotificationsIcon />
                </Avatar>

                <Box sx={{ width: "100%" }}>
                  {/* Title + Time in one row */}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {item.title}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{ color: "#444", fontSize: "12px" }}
                    >
                      {timeAgo(item.createdAt)}
                    </Typography>
                  </Stack>

                  {/* Message below */}
                  <Typography variant="body2" sx={{ color: "gray", mt: 0.5 }}>
                    {item.message}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        ))}
    </Box>
  );
}

export default Notification;
